import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getInvoice } from "@/lib/api";

/**
 * A client's invoice, reached only through the token in the link.
 *
 * Not wrapped in PageShell: this is a document, not a page of the website. No
 * navigation, no footer, no smooth scroll — nothing that would either print or
 * invite the client to wander off into the portfolio while they are trying to
 * pay someone.
 *
 * Saving as PDF is the browser's own print pipeline. Everything that must
 * survive on paper is type, a rule or a border, because print drops background
 * fills by default (see the invoice block in index.css).
 */

const CURRENCY_LOCALE = {
    INR: "en-IN",
    USD: "en-US",
    EUR: "en-IE",
    GBP: "en-GB",
    AED: "en-AE",
    SGD: "en-SG",
    AUD: "en-AU",
    CAD: "en-CA",
};

function money(minor, currency) {
    const value = (Number(minor) || 0) / 100;
    try {
        return new Intl.NumberFormat(CURRENCY_LOCALE[currency] || "en-IN", {
            style: "currency",
            currency: currency || "INR",
            minimumFractionDigits: 2,
        }).format(value);
    } catch {
        return `${currency} ${value.toFixed(2)}`;
    }
}

/** 2026-08-05 → 5 August 2026. Never the ambiguous numeric forms. */
function longDate(ymd) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || "")) return "";
    const [y, m, d] = ymd.split("-").map(Number);
    return `${d} ${
        [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ][m - 1]
    } ${y}`;
}

const num = { fontVariantNumeric: "tabular-nums" };

export default function InvoicePage() {
    const { token } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [state, setState] = useState("loading");

    useEffect(() => {
        let live = true;
        getInvoice(token)
            .then((data) => {
                if (!live) return;
                setInvoice(data);
                setState("ready");
            })
            .catch(() => {
                if (live) setState("missing");
            });
        return () => {
            live = false;
        };
    }, [token]);

    // The browser names the saved PDF after the document title, so this is the
    // difference between the client filing "Depmont Invoice D-2627-001.pdf" and
    // filing "Depmont — Independent Creative Company.pdf".
    useEffect(() => {
        const previous = document.title;
        if (invoice?.number) document.title = `Depmont Invoice ${invoice.number}`;
        return () => {
            document.title = previous;
        };
    }, [invoice]);

    if (state === "loading") {
        return (
            <div className="min-h-screen bg-[#F6F3EC] flex items-center justify-center">
                <p className="eyebrow text-[#1B1B1B]/50">Loading…</p>
            </div>
        );
    }

    if (state === "missing" || !invoice) {
        return (
            <div className="min-h-screen bg-[#F6F3EC] flex items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <p className="eyebrow text-[#1B1B1B]/60 mb-4">Not found</p>
                    <h1 className="font-serif-editorial text-3xl md:text-4xl leading-[1.1] mb-5">
                        This invoice link is not valid.
                    </h1>
                    <p className="font-sans-tight text-sm text-[#1B1B1B]/70 font-light leading-[1.7]">
                        The link may have been copied incompletely. Please check it
                        against the one you were sent, or write to{" "}
                        <a
                            href="mailto:contact@depmont.com"
                            className="link-underline text-[#A8844F]"
                        >
                            contact@depmont.com
                        </a>
                        .
                    </p>
                </div>
            </div>
        );
    }

    const { seller, client, currency } = invoice;
    const isVoid = invoice.status === "void";
    const isPaid = invoice.status === "paid";
    const hasLines = invoice.item_mode === "lines";
    const showBankBlock =
        !isPaid && !isVoid && (seller.upi_id || seller.bank_account_number);

    return (
        <div className="min-h-screen bg-[#F6F3EC] text-[#1B1B1B]">
            {/* Interface. None of this prints. */}
            <div className="no-print border-b border-[#E6E2D6]">
                <div className="max-w-[210mm] mx-auto px-6 py-5 flex items-center justify-between gap-6">
                    <div>
                        <p className="font-serif-editorial text-xl tracking-[-0.045em] leading-none">
                            Depmont<span className="text-[#A8844F]">.</span>
                        </p>
                        <p className="eyebrow text-[#1B1B1B]/50 mt-1">
                            Invoice {invoice.number}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => window.print()}
                        data-testid="invoice-print"
                        className="shrink-0 inline-flex items-center gap-3 px-6 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[11px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500"
                    >
                        Save as PDF
                    </button>
                </div>
            </div>

            <div className="no-print max-w-[210mm] mx-auto px-6 pt-6">
                <p className="font-sans-tight text-xs text-[#1B1B1B]/55 font-light leading-[1.7]">
                    Choose <span className="text-[#1B1B1B]">Save as PDF</span> as the
                    destination in the dialogue that opens. Printing to paper works
                    from the same button.
                </p>
            </div>

            <div className="px-4 sm:px-6 py-8 md:py-12">
                <article
                    className="invoice-sheet border border-[#E6E2D6]"
                    data-testid="invoice-sheet"
                >
                    {/* ---------- Masthead ---------- */}
                    <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-8 invoice-avoid-break">
                        <div className="max-w-[55%]">
                            <p className="font-serif-editorial text-[26px] tracking-[-0.045em] leading-none">
                                {seller.trading_name || "Depmont"}
                                <span className="text-[#A8844F]">.</span>
                            </p>
                            {seller.legal_name &&
                                seller.legal_name !== seller.trading_name && (
                                    <p className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-2 font-light">
                                        {seller.legal_name}
                                    </p>
                                )}
                            {seller.address && (
                                <p className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-2 font-light leading-[1.7] whitespace-pre-line">
                                    {seller.address}
                                </p>
                            )}
                            <div className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-2 font-light leading-[1.7]">
                                {seller.email && <p>{seller.email}</p>}
                                {seller.phone && <p style={num}>{seller.phone}</p>}
                                {seller.website && <p>{seller.website}</p>}
                            </div>
                            {(seller.gstin || seller.pan) && (
                                <div className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-2 font-light leading-[1.7]">
                                    {seller.gstin && <p style={num}>GSTIN {seller.gstin}</p>}
                                    {seller.pan && <p style={num}>PAN {seller.pan}</p>}
                                </div>
                            )}
                        </div>

                        <div className="sm:text-right shrink-0">
                            <p className="eyebrow text-[#1B1B1B]/60">Invoice</p>
                            <p
                                className="font-serif-editorial text-[28px] leading-none mt-2"
                                style={num}
                                data-testid="invoice-number"
                            >
                                {invoice.number}
                            </p>
                            <dl className="mt-5 font-sans-tight text-[11px] font-light leading-[1.9]">
                                <div className="flex sm:justify-end gap-3">
                                    <dt className="text-[#1B1B1B]/55">Issued</dt>
                                    <dd style={num}>{longDate(invoice.issue_date)}</dd>
                                </div>
                                {invoice.due_date && !isPaid && !isVoid && (
                                    <div className="flex sm:justify-end gap-3">
                                        <dt className="text-[#1B1B1B]/55">Due</dt>
                                        <dd style={num}>{longDate(invoice.due_date)}</dd>
                                    </div>
                                )}
                                {isPaid && invoice.paid_date && (
                                    <div className="flex sm:justify-end gap-3">
                                        <dt className="text-[#1B1B1B]/55">Paid</dt>
                                        <dd style={num}>{longDate(invoice.paid_date)}</dd>
                                    </div>
                                )}
                            </dl>
                        </div>
                    </header>

                    {/* ---------- Status ----------
                        A bordered mark rather than a filled stamp, so it is still
                        there when the client prints without background graphics. */}
                    {(isPaid || isVoid || invoice.overdue) && (
                        <div className="mt-8 invoice-avoid-break">
                            <span
                                data-testid="invoice-status"
                                className={`inline-block border px-4 py-2 font-sans-tight text-[11px] tracking-[0.28em] uppercase ${
                                    isVoid
                                        ? "border-[#991B1B]/50 text-[#991B1B]"
                                        : isPaid
                                          ? "border-[#A8844F] text-[#A8844F]"
                                          : "border-[#991B1B]/50 text-[#991B1B]"
                                }`}
                            >
                                {isVoid ? "Void" : isPaid ? "Paid" : "Overdue"}
                            </span>
                            {isVoid && invoice.void_reason && (
                                <p className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-3 font-light">
                                    {invoice.void_reason}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="invoice-rule mt-8" />

                    {/* ---------- Parties ---------- */}
                    <section className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-8 invoice-avoid-break">
                        <div>
                            <p className="eyebrow text-[#1B1B1B]/60 mb-3">Billed to</p>
                            <p
                                className="font-serif-editorial text-xl leading-[1.2]"
                                data-testid="invoice-client"
                            >
                                {client.name}
                            </p>
                            <div className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-2 font-light leading-[1.7]">
                                {client.contact && <p>{client.contact}</p>}
                                {client.address && (
                                    <p className="whitespace-pre-line">{client.address}</p>
                                )}
                                {client.email && <p>{client.email}</p>}
                                {client.tax_id && <p style={num}>GSTIN {client.tax_id}</p>}
                            </div>
                        </div>

                        {invoice.title && (
                            <div>
                                <p className="eyebrow text-[#1B1B1B]/60 mb-3">Project</p>
                                <p className="font-sans-tight text-sm font-light leading-[1.7]">
                                    {invoice.title}
                                </p>
                                {invoice.place_of_supply && (
                                    <p className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-2 font-light">
                                        Place of supply · {invoice.place_of_supply}
                                    </p>
                                )}
                            </div>
                        )}
                    </section>

                    {/* ---------- Items ---------- */}
                    <section className="mt-10">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b border-[#E6E2D6]">
                                    <th className="text-left pb-3 eyebrow text-[#1B1B1B]/60 font-medium">
                                        Description
                                    </th>
                                    {hasLines && (
                                        <>
                                            <th className="text-right pb-3 eyebrow text-[#1B1B1B]/60 font-medium w-20">
                                                Qty
                                            </th>
                                            <th className="text-right pb-3 eyebrow text-[#1B1B1B]/60 font-medium w-32">
                                                Rate
                                            </th>
                                        </>
                                    )}
                                    <th className="text-right pb-3 eyebrow text-[#1B1B1B]/60 font-medium w-36">
                                        Amount
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.items.map((item, i) => (
                                    <tr
                                        key={i}
                                        className="border-b border-[#E6E2D6] invoice-avoid-break"
                                    >
                                        <td className="py-4 pr-6 font-sans-tight text-sm font-light leading-[1.7] whitespace-pre-line align-top">
                                            {item.description}
                                        </td>
                                        {hasLines && (
                                            <>
                                                <td
                                                    className="py-4 text-right font-sans-tight text-sm font-light align-top"
                                                    style={num}
                                                >
                                                    {item.qty}
                                                </td>
                                                <td
                                                    className="py-4 text-right font-sans-tight text-sm font-light align-top"
                                                    style={num}
                                                >
                                                    {money(item.rate_minor, currency)}
                                                </td>
                                            </>
                                        )}
                                        <td
                                            className="py-4 text-right font-sans-tight text-sm font-light align-top"
                                            style={num}
                                        >
                                            {money(item.amount_minor, currency)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    {/* ---------- Totals ---------- */}
                    <section className="mt-8 flex justify-end invoice-avoid-break">
                        <div className="w-full sm:w-[62%]">
                            <Row
                                label="Subtotal"
                                value={money(invoice.subtotal_minor, currency)}
                            />
                            {invoice.discount_minor > 0 && (
                                <Row
                                    label="Discount"
                                    value={`− ${money(invoice.discount_minor, currency)}`}
                                />
                            )}
                            {invoice.tax_amount_minor > 0 && (
                                <Row
                                    label={`GST ${(invoice.tax_rate_bps / 100).toFixed(0)}%`}
                                    value={money(invoice.tax_amount_minor, currency)}
                                />
                            )}

                            <div className="invoice-rule mt-4" />
                            <div className="flex items-baseline justify-between gap-6 pt-4">
                                <p className="eyebrow text-[#1B1B1B]/60">
                                    {isPaid ? "Total paid" : "Total due"}
                                </p>
                                <p
                                    className={`font-serif-editorial text-[26px] leading-none ${
                                        isVoid ? "invoice-void-strike text-[#1B1B1B]/50" : ""
                                    }`}
                                    style={num}
                                    data-testid="invoice-total"
                                >
                                    {money(invoice.total_minor, currency)}
                                </p>
                            </div>
                            {currency !== "INR" && (
                                <p className="font-sans-tight text-[11px] text-[#1B1B1B]/60 mt-3 font-light text-right">
                                    Amounts in {currency}. Any intermediary or
                                    correspondent bank charges are payable by the sender.
                                </p>
                            )}
                        </div>
                    </section>

                    {/* ---------- How to pay ---------- */}
                    {showBankBlock && (
                        <section className="mt-12 invoice-avoid-break">
                            <div className="invoice-rule mb-6" />
                            <p className="eyebrow text-[#1B1B1B]/60 mb-4">Payment</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                {seller.upi_id && (
                                    <div>
                                        <p className="font-sans-tight text-[11px] text-[#1B1B1B]/55 font-light">
                                            UPI
                                        </p>
                                        <p
                                            className="font-sans-tight text-sm mt-1"
                                            style={num}
                                            data-testid="invoice-upi"
                                        >
                                            {seller.upi_id}
                                        </p>
                                        {seller.upi_name && (
                                            <p className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-1 font-light">
                                                {seller.upi_name}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {seller.bank_account_number && (
                                    <div className="font-sans-tight text-[11px] font-light leading-[1.9]">
                                        <p className="text-[#1B1B1B]/55">Bank transfer</p>
                                        <dl className="mt-1">
                                            <Pair label="Account name" value={seller.bank_account_name} />
                                            <Pair label="Account number" value={seller.bank_account_number} mono />
                                            <Pair label="IFSC" value={seller.bank_ifsc} mono />
                                            <Pair label="Bank" value={seller.bank_name} />
                                            <Pair label="Branch" value={seller.bank_branch} />
                                            <Pair label="SWIFT" value={seller.bank_swift} mono />
                                            <Pair label="IBAN" value={seller.bank_iban} mono />
                                        </dl>
                                    </div>
                                )}
                            </div>
                            <p className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-6 font-light leading-[1.7]">
                                Please quote{" "}
                                <span className="text-[#1B1B1B]" style={num}>
                                    {invoice.number}
                                </span>{" "}
                                as the reference with your transfer.
                            </p>
                        </section>
                    )}

                    {/* ---------- Payment received ---------- */}
                    {isPaid && (
                        <section className="mt-12 invoice-avoid-break">
                            <div className="invoice-rule mb-6" />
                            <p className="eyebrow text-[#1B1B1B]/60 mb-4">
                                Payment received
                            </p>
                            <div className="font-sans-tight text-[11px] font-light leading-[1.9]">
                                <Pair label="Date" value={longDate(invoice.paid_date)} />
                                <Pair label="Method" value={invoice.paid_method} />
                                <Pair label="Reference" value={invoice.paid_reference} mono />
                            </div>
                            <p className="font-sans-tight text-[11px] text-[#1B1B1B]/70 mt-4 font-light leading-[1.7]">
                                This invoice is settled in full. No further payment is due.
                            </p>
                        </section>
                    )}

                    {/* ---------- Terms and notes ---------- */}
                    {(invoice.terms || invoice.notes) && !isVoid && (
                        <section className="mt-12 invoice-avoid-break">
                            <div className="invoice-rule mb-6" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                {invoice.terms && (
                                    <div>
                                        <p className="eyebrow text-[#1B1B1B]/60 mb-3">Terms</p>
                                        <p className="font-sans-tight text-[11px] text-[#1B1B1B]/75 font-light leading-[1.8] whitespace-pre-line">
                                            {invoice.terms}
                                        </p>
                                    </div>
                                )}
                                {invoice.notes && (
                                    <div>
                                        <p className="eyebrow text-[#1B1B1B]/60 mb-3">Notes</p>
                                        <p className="font-sans-tight text-[11px] text-[#1B1B1B]/75 font-light leading-[1.8] whitespace-pre-line">
                                            {invoice.notes}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {/* ---------- Foot ----------
                        An unregistered business may not issue a "Tax Invoice" or
                        collect GST. Saying so plainly is the correct disclosure and
                        it answers the question the client's accountant would
                        otherwise have to ask. */}
                    <footer className="mt-12 pt-6 border-t border-[#E6E2D6] invoice-avoid-break">
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                            <p className="font-sans-tight text-[10px] text-[#1B1B1B]/55 font-light leading-[1.8] max-w-sm">
                                {seller.gst_registered
                                    ? "Tax charged as shown above."
                                    : "Not registered under GST. No tax has been charged on this invoice."}
                            </p>
                            <p className="font-sans-tight text-[10px] text-[#1B1B1B]/55 font-light sm:text-right">
                                {seller.legal_name || "Depmont"}
                                {seller.email ? ` · ${seller.email}` : ""}
                            </p>
                        </div>
                    </footer>
                </article>
            </div>
        </div>
    );
}

const Row = ({ label, value }) => (
    <div className="flex items-baseline justify-between gap-6 py-2">
        <p className="font-sans-tight text-[11px] text-[#1B1B1B]/60 font-light">
            {label}
        </p>
        <p
            className="font-sans-tight text-sm font-light"
            style={{ fontVariantNumeric: "tabular-nums" }}
        >
            {value}
        </p>
    </div>
);

const Pair = ({ label, value, mono }) =>
    value ? (
        <div className="flex gap-3">
            <dt className="text-[#1B1B1B]/55 w-28 shrink-0">{label}</dt>
            <dd style={mono ? { fontVariantNumeric: "tabular-nums" } : undefined}>
                {value}
            </dd>
        </div>
    ) : null;
